// AiInfoTooltip — the reusable "this is AI" INFO mark: an AiMarker whose
// hover/focus tooltip carries a line of prose plus optional action links (e.g.
// "Read more", "Turn it off in settings"). Pure and props-driven.
//
// The pattern this generalises started life inline on the Journal summary (the
// SummaryAiMark), where it grew cramped and ad-hoc. It is the app-wide shape for
// marking AI-generated content: clear but unobtrusive (the mark-AI-content
// principle) — the glyph is quiet, the explanation lives one hover away, and any
// follow-up actions ride inside the same tooltip rather than crowding the mark.
//
// Composition, not reinvention: the VISUAL mark is the shipped `AiMarker`, and
// the floating plane is the shipped `Tooltip` primitive (the app mounts one
// `TooltipProvider` at its root). This component only wires the two together with
// a typed content body. Radix keeps the tooltip content hoverable, so the action
// links are reachable by both pointer and keyboard.

import type { ReactNode } from "react";
import { AiMarker, type AiMarkerVariant } from "../AiMarker/AiMarker.js";
import { Text } from "../Text/Text.js";
import { Tooltip, type TooltipSide } from "../Tooltip/Tooltip.js";
import "./AiInfoTooltip.css";

// One action link inside the tooltip: a visible label + the intent it fires.
// A named type (never a bare inline object) so a list of them is typed on the
// public surface and a future per-action attribute has somewhere to live.
type AiInfoTooltipAction = {
    readonly label: string;
    readonly onSelect: () => void;
};

export type AiInfoTooltipProps = {
    // The one-line explanation of what the AI mark means here (why this content
    // is AI-generated). Kept short — a whispered aside, not a paragraph.
    readonly prose: string;
    // Optional follow-up links (Read more · Turn it off …). An empty/absent list
    // renders the prose alone.
    readonly actions?: readonly AiInfoTooltipAction[];
    // Where the tooltip floats relative to the mark. Defaults to "bottom" (the
    // Journal summary's placement).
    readonly side?: TooltipSide;
    // The AiMarker form. Defaults to the element-level "chip".
    readonly markVariant?: AiMarkerVariant;
    // Icon-only mark (the quietest form): the glyph without the visible "AI"
    // word. Defaults to true — the meaning is taught elsewhere (onboarding) and
    // the a11y name still announces "AI-generated".
    readonly markIconOnly?: boolean;
};

export const AiInfoTooltip = ({
    prose,
    actions,
    side = "bottom",
    markVariant = "chip",
    markIconOnly = true,
}: AiInfoTooltipProps): ReactNode => (
    <Tooltip
        side={side}
        content={
            <div className="bilda-ai-info-tooltip">
                <Text as="p" size="xs">
                    {prose}
                </Text>
                {actions && actions.length > 0 ? (
                    <div className="bilda-ai-info-tooltip__actions">
                        {actions.map((action) => (
                            <button
                                key={action.label}
                                type="button"
                                className="bilda-ai-info-tooltip__link"
                                onClick={action.onSelect}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>
        }
    >
        <button type="button" className="bilda-ai-info-tooltip__mark">
            <AiMarker variant={markVariant} iconOnly={markIconOnly} />
        </button>
    </Tooltip>
);
